import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { 
  UserCheck, 
  Building2, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  CheckCircle2, 
  Award, 
  Send, 
  Sparkles, 
  Briefcase, 
  DollarSign, 
  Info,
  Clock,
  ArrowRight,
  X,
  Loader2,
  Upload
} from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';
import { Turnstile } from '@/components/ui/Turnstile';
import { CertificationType } from '@/data/practitioners';
import { ImagePicker } from '@/components/ImagePicker';

export function PractitionerOnboardingPage() {
  const [applicantType, setApplicantType] = useState<'individual' | 'company'>('individual');
  
  // Contact & Basic Details
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  
  // Qualifications & Certifications
  const [certifications, setCertifications] = useState<CertificationType[]>([
    '1-on-1 Coaching (Personal Growth & Development)'
  ]);
  const [icfCredentials, setIcfCredentials] = useState('');
  const [experienceYears, setExperienceYears] = useState('3-5 years');
  const [specialisms, setSpecialisms] = useState('Executive Coaching, Leadership Development, Team Alignment');
  
  // Bio & Image
  const [bio, setBio] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [photoKey, setPhotoKey] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoNotice, setPhotoNotice] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [resetKey, setResetKey] = useState(0);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoError('');
    setPhotoNotice('');

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setPhotoError('Invalid image format. Please upload a JPG, PNG, or WebP photo.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setPhotoError('File size exceeds the 2 MB limit. Please select a smaller photo.');
      return;
    }

    setUploadingPhoto(true);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result as string;
          const response = await fetch('/api/upload-photo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileData: base64Data,
              fileName: file.name,
              mimeType: file.type,
              turnstileToken,
            })
          });

          const data = await response.json();
          if (response.status === 503 || !response.ok) {
            setPhotoNotice("Photo upload is temporarily unavailable. You can send your photo later.");
            setUploadingPhoto(false);
            return;
          }

          if (data.photoKey) {
            setPhotoKey(data.photoKey);
            setImageUrl(base64Data);
          } else if (data.url) {
            setImageUrl(data.url);
          }
        } catch (err: any) {
          setPhotoNotice("Photo upload is temporarily unavailable. You can send your photo later.");
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setPhotoNotice("Photo upload is temporarily unavailable. You can send your photo later.");
      setUploadingPhoto(false);
    }
  };

  // Rate Details (Optional)
  const [currency, setCurrency] = useState<'USD' | 'ZAR'>('ZAR');
  const [rate1on1PG, setRate1on1PG] = useState('');
  const [rate1on1Leadership, setRate1on1Leadership] = useState('');
  const [rateTeam, setRateTeam] = useState('');
  
  // Additional Notes
  const [notes, setNotes] = useState('');
  
  // Form State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const toggleCertification = (cert: CertificationType) => {
    if (certifications.includes(cert)) {
      if (certifications.length === 1) return; // Keep at least 1
      setCertifications(certifications.filter(c => c !== cert));
    } else {
      setCertifications([...certifications, cert]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!fullName.trim() || !email.trim() || !location.trim() || !bio.trim()) {
      setErrorMessage('Please fill in all required fields (Full Name, Email, Location, and Bio).');
      return;
    }

    if (!turnstileToken) {
      setErrorMessage('Please complete the Turnstile verification.');
      return;
    }

    setIsSubmitting(true);

    try {
      // NOTE: Send ONLY photoKey returned by /api/upload-photo to keep payload well under 100 KB.
      // Do NOT send imageUrl base64 data URL.
      const payload = {
        applicantType,
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        location: location.trim(),
        companyName: companyName.trim(),
        websiteUrl: websiteUrl.trim(),
        certifications,
        icfCredentials: icfCredentials.trim(),
        experienceYears,
        specialisms: specialisms.split(',').map(s => s.trim()).filter(Boolean).join(', '),
        bio: bio.trim(),
        photoKey: photoKey.trim(),
        proposedPricing: {
          currency,
          oneOnOnePersonalGrowth: rate1on1PG ? Number(rate1on1PG) : null,
          oneOnOneLeadership: rate1on1Leadership ? Number(rate1on1Leadership) : null,
          teamCoaching: rateTeam ? Number(rateTeam) : null
        },
        notes: notes.trim(),
        turnstileToken,
        company_website: companyWebsite,
      };

      // 1. Primary submission via server endpoint
      const response = await fetch('/api/practitioner-onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Something went wrong submitting your application. Please try again or email office@enneainternational.com.');
      }

      setSubmittedRef(data.refCode || 'FL-APP-CONFIRMED');
      window.scrollTo({ top: 100, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Error submitting practitioner onboarding application:', err);
      setErrorMessage(err?.message || 'Something went wrong submitting your application. Please try again or email office@enneainternational.com.');
      setTurnstileToken('');
      setResetKey(prev => prev + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-ground pt-28 pb-24">
      <Helmet>
        <title>Practitioner Onboarding Application | FIVE LENS Enneagram</title>
        <meta 
          name="description" 
          content="Apply to join the FIVE LENS Enneagram certified practitioner network as an accredited coach or corporate partner. Submit your credentials for admin review." 
        />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Breadcrumb items={[
            { label: 'Home', href: '/' },
            { label: 'For Practitioners', href: '/practitioners' },
            { label: 'Practitioner Onboarding' }
          ]} />
        </div>

        {/* Page Hero Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-rule-3/10 text-rule-3 text-xs font-bold uppercase tracking-wider mb-4 border border-rule-3/20">
            <Sparkles className="w-4 h-4" />
            FIVE LENS Certified Network
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-ink font-bold mb-4">
            Practitioner Onboarding Application
          </h1>
          <p className="text-lg text-ink/75 max-w-2xl mx-auto leading-relaxed">
            Register your profile or corporate practice with the FIVE LENS network. Your application will be submitted directly to our administrative board for final review and approval.
          </p>
        </div>

        {submittedRef ? (
          /* SUCCESS STATE */
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white border-2 border-emerald-500/30 p-8 sm:p-12 rounded-2xl shadow-xl text-center space-y-6"
          >
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                Application Received
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-ink pt-2">
                Thank You for Applying!
              </h2>
              <p className="text-ink/70 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
                Your practitioner onboarding profile has been submitted successfully to the FIVE LENS Admin Board for final approval.
              </p>
            </div>

            <div className="bg-ground-alt p-4 rounded-xl border border-ground-deep max-w-md mx-auto space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-ink/60">Application Reference ID</p>
              <p className="text-xl font-mono font-bold text-rule-3">{submittedRef}</p>
            </div>

            <div className="border-t border-ground-deep pt-6 max-w-lg mx-auto text-left space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink flex items-center gap-2">
                <Clock className="w-4 h-4 text-rule-3" /> What Happens Next?
              </h3>
              <ul className="text-sm text-ink/80 space-y-2 list-disc list-inside">
                <li>Our team will review your credentials, certifications, and bio.</li>
                <li>Upon approval, your profile will be activated in the official <strong className="text-ink">Find a Practitioner</strong> directory.</li>
                <li>We will send a confirmation notification to <strong className="text-ink">{email}</strong>.</li>
              </ul>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                type="button"
                variant="outline"
                onClick={() => {
                  setSubmittedRef(null);
                  setFullName('');
                  setEmail('');
                  setBio('');
                }}
              >
                Submit Another Application
              </Button>
              <Button 
                type="button"
                onClick={() => window.location.href = '/find-a-practitioner'}
                className="gap-2"
              >
                View Practitioner Directory
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        ) : (
          /* ONBOARDING FORM */
          <form onSubmit={handleSubmit} className="bg-white border border-ground-deep p-6 sm:p-10 rounded-2xl shadow-md space-y-10">
            {errorMessage && (
              <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl text-sm flex items-start gap-3">
                <Info className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div>{errorMessage}</div>
              </div>
            )}

            {/* SECTION 1: Applicant Category */}
            <div className="space-y-4">
              <div className="border-b border-ground-deep pb-3">
                <h2 className="text-xl font-serif text-ink font-bold flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-rule-3" />
                  1. Application Type
                </h2>
                <p className="text-xs text-ink/60 mt-0.5">Are you registering as an independent coach or an organizational partner?</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setApplicantType('individual')}
                  className={`p-4 rounded-xl border text-left transition-all flex items-start gap-3.5 ${
                    applicantType === 'individual'
                      ? 'border-rule-3 bg-rule-3/5 ring-2 ring-rule-3/20 shadow-sm'
                      : 'border-ground-deep hover:border-ink/30 bg-ground/20'
                  }`}
                >
                  <div className={`p-2.5 rounded-lg shrink-0 ${applicantType === 'individual' ? 'bg-rule-3 text-white' : 'bg-ground-deep text-ink/70'}`}>
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-ink text-sm">Individual Practitioner</p>
                    <p className="text-xs text-ink/70 mt-1">Independent coach, consultant, or executive facilitator.</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setApplicantType('company')}
                  className={`p-4 rounded-xl border text-left transition-all flex items-start gap-3.5 ${
                    applicantType === 'company'
                      ? 'border-rule-3 bg-rule-3/5 ring-2 ring-rule-3/20 shadow-sm'
                      : 'border-ground-deep hover:border-ink/30 bg-ground/20'
                  }`}
                >
                  <div className={`p-2.5 rounded-lg shrink-0 ${applicantType === 'company' ? 'bg-rule-3 text-white' : 'bg-ground-deep text-ink/70'}`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-ink text-sm">Company / Organizational Partner</p>
                    <p className="text-xs text-ink/70 mt-1">Corporate firm, HR consultancy, or multi-coach agency.</p>
                  </div>
                </button>
              </div>
            </div>

            {/* SECTION 2: Contact & Personal Info */}
            <div className="space-y-4">
              <div className="border-b border-ground-deep pb-3">
                <h2 className="text-xl font-serif text-ink font-bold flex items-center gap-2">
                  <User className="w-5 h-5 text-rule-3" />
                  2. Contact & Identity Information
                </h2>
                <p className="text-xs text-ink/60 mt-0.5">Primary contact details for network administration and directory listing.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      placeholder="e.g. Dr. Sarah Jenkins"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      className="w-full border border-ground-deep p-3 pl-10 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                    />
                    <User className="w-4 h-4 text-ink/40 absolute left-3.5 top-3.5" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      placeholder="e.g. sarah@coaching.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full border border-ground-deep p-3 pl-10 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                    />
                    <Mail className="w-4 h-4 text-ink/40 absolute left-3.5 top-3.5" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Phone / WhatsApp
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      placeholder="e.g. +27 82 123 4567"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      className="w-full border border-ground-deep p-3 pl-10 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                    />
                    <Phone className="w-4 h-4 text-ink/40 absolute left-3.5 top-3.5" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Location / Country <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. London, United Kingdom or Johannesburg, South Africa"
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    className="w-full border border-ground-deep p-3 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Company / Consultancy Name {applicantType === 'company' && <span className="text-rose-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g. Apex Leadership Partners"
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      className="w-full border border-ground-deep p-3 pl-10 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                    />
                    <Building2 className="w-4 h-4 text-ink/40 absolute left-3.5 top-3.5" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Website or LinkedIn URL
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      placeholder="https://linkedin.com/in/yourprofile"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      className="w-full border border-ground-deep p-3 pl-10 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                    />
                    <Globe className="w-4 h-4 text-ink/40 absolute left-3.5 top-3.5" />
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 3: Certifications & Qualifications */}
            <div className="space-y-4">
              <div className="border-b border-ground-deep pb-3">
                <h2 className="text-xl font-serif text-ink font-bold flex items-center gap-2">
                  <Award className="w-5 h-5 text-rule-3" />
                  3. FIVE LENS Certifications & Credentials
                </h2>
                <p className="text-xs text-ink/60 mt-0.5">Select the certification areas you hold or are completing.</p>
              </div>

              <div className="space-y-3">
                <label className="block text-xs font-bold uppercase tracking-wider text-ink">
                  Certifications <span className="text-rose-500">*</span>
                </label>
                
                <div className="grid grid-cols-1 gap-2.5">
                  {[
                    '1-on-1 Coaching (Personal Growth & Development)',
                    '1-on-1 Coaching (Leadership)',
                    'Team Coaching'
                  ].map((cert) => {
                    const isChecked = certifications.includes(cert as CertificationType);
                    return (
                      <label 
                        key={cert}
                        className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                          isChecked 
                            ? 'border-rule-3 bg-rule-3/5 text-ink font-medium' 
                            : 'border-ground-deep bg-ground/10 text-ink/70 hover:bg-ground/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleCertification(cert as CertificationType)}
                          className="w-4 h-4 text-rule-3 rounded accent-rule-3"
                        />
                        <span className="text-sm">{cert}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    ICF or Professional Accreditation
                  </label>
                  <select
                    value={icfCredentials}
                    onChange={e => setIcfCredentials(e.target.value)}
                    className="w-full border border-ground-deep p-3 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                  >
                    <option value="">None / Independent Practitioner</option>
                    <option value="ACC (Associate Certified Coach)">ICF ACC (Associate Certified Coach)</option>
                    <option value="PCC (Professional Certified Coach)">ICF PCC (Professional Certified Coach)</option>
                    <option value="MCC (Master Certified Coach)">ICF MCC (Master Certified Coach)</option>
                    <option value="COMENSA Accredited">COMENSA Accredited</option>
                    <option value="SIOPSA Registered Psychologist">SIOPSA Registered Psychologist</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Enneagram & Coaching Experience
                  </label>
                  <select
                    value={experienceYears}
                    onChange={e => setExperienceYears(e.target.value)}
                    className="w-full border border-ground-deep p-3 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                  >
                    <option value="1-2 years">1–2 years</option>
                    <option value="3-5 years">3–5 years</option>
                    <option value="5-10 years">5–10 years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                    Specialisms (Comma separated)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Executive Coaching, Team Alignment, Conflict Resolution, Personal Mastery"
                    value={specialisms}
                    onChange={e => setSpecialisms(e.target.value)}
                    className="w-full border border-ground-deep p-3 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 4: Profile Bio & Photo */}
            <div className="space-y-4">
              <div className="border-b border-ground-deep pb-3">
                <h2 className="text-xl font-serif text-ink font-bold flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-rule-3" />
                  4. Directory Profile Bio & Photo
                </h2>
                <p className="text-xs text-ink/60 mt-0.5">This profile copy will be rendered on your official practitioner card upon approval.</p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                  Profile Photo (JPG, PNG, WebP — max 2 MB) <span className="text-rose-500">*</span>
                </label>
                {imageUrl ? (
                  <div className="flex items-center gap-4">
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-ground-deep">
                      <img src={imageUrl} alt="Profile photo preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setImageUrl('')}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-black transition-colors"
                        title="Remove photo"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-xs text-emerald-700 font-medium">
                      ✓ Photo uploaded successfully.
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed border-ground-deep rounded-lg bg-ground/20 text-center">
                    <input
                      type="file"
                      id="applicant-photo"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      className="hidden"
                    />
                    <label
                      htmlFor="applicant-photo"
                      className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-rule-3 text-white text-xs font-bold rounded hover:bg-rule-3/90 transition-colors disabled:opacity-50"
                    >
                      {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingPhoto ? 'Uploading Photo...' : 'Choose JPG, PNG or WebP Photo (Max 2 MB)'}
                    </label>
                    <p className="text-[11px] text-ink/50 mt-2">
                      Upload a real headshot (JPG, PNG, or WebP). Maximum file size is 2 MB.
                    </p>
                    {photoNotice && (
                      <p className="text-xs text-amber-700 font-medium mt-2 bg-amber-50 p-2 rounded border border-amber-200">{photoNotice}</p>
                    )}
                    {photoError && (
                      <p className="text-xs text-rose-600 font-medium mt-2">{photoError}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                  Professional Biography <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Describe your background, coaching philosophy, executive experience, and how you apply the FIVE LENS framework with clients..."
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  className="w-full border border-ground-deep p-3 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink leading-relaxed"
                />
                <p className="text-[11px] text-ink/50 mt-1">
                  Aim for 2–4 sentences highlighting your expertise and target clientele.
                </p>
              </div>
            </div>

            {/* SECTION 5: Proposed Rates (ZAR only) */}
            <div className="space-y-4">
              <div className="border-b border-ground-deep pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-serif text-ink font-bold flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-rule-3" />
                    5. Rates & Pricing (ZAR)
                  </h2>
                  <p className="text-xs text-ink/60 mt-0.5">1-on-1 coaching per session must be between R2,000 and R5,000; facilitation per day between R12,000 and R30,000.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-ground/30 p-3.5 rounded-xl border border-ground-deep space-y-1.5">
                  <label className="block text-xs font-bold text-ink">1-on-1 Personal Growth (R2k - R5k)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs font-bold text-ink/50">R</span>
                    <input
                      type="number"
                      min={2000}
                      max={5000}
                      placeholder="3500"
                      value={rate1on1PG}
                      onChange={e => setRate1on1PG(e.target.value)}
                      className="w-full border border-ground-deep p-2 pl-7 rounded outline-none focus:border-rule-3 text-sm bg-white"
                    />
                  </div>
                  <p className="text-[10px] text-ink/50">per 1-hour session</p>
                </div>

                <div className="bg-ground/30 p-3.5 rounded-xl border border-ground-deep space-y-1.5">
                  <label className="block text-xs font-bold text-ink">1-on-1 Leadership (R2k - R5k)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs font-bold text-ink/50">R</span>
                    <input
                      type="number"
                      min={2000}
                      max={5000}
                      placeholder="4000"
                      value={rate1on1Leadership}
                      onChange={e => setRate1on1Leadership(e.target.value)}
                      className="w-full border border-ground-deep p-2 pl-7 rounded outline-none focus:border-rule-3 text-sm bg-white"
                    />
                  </div>
                  <p className="text-[10px] text-ink/50">per executive session</p>
                </div>

                <div className="bg-ground/30 p-3.5 rounded-xl border border-ground-deep space-y-1.5">
                  <label className="block text-xs font-bold text-ink">Team Facilitation (R12k - R30k)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs font-bold text-ink/50">R</span>
                    <input
                      type="number"
                      min={12000}
                      max={30000}
                      placeholder="18000"
                      value={rateTeam}
                      onChange={e => setRateTeam(e.target.value)}
                      className="w-full border border-ground-deep p-2 pl-7 rounded outline-none focus:border-rule-3 text-sm bg-white"
                    />
                  </div>
                  <p className="text-[10px] text-ink/50">per day / workshop</p>
                </div>
              </div>
            </div>

            {/* SECTION 6: Notes & Submission */}
            <div className="space-y-4 pt-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-ink mb-2">
                  Additional Comments or Special Requests
                </label>
                <textarea
                  rows={2}
                  placeholder="Any specific questions, cohort preferences, or details for the admin team..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="w-full border border-ground-deep p-3 rounded-lg outline-none focus:border-rule-3 bg-ground/30 text-sm text-ink"
                />
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl text-xs text-ink/80 flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p>
                  By submitting this form, your application will be routed to the <strong className="text-ink">FIVE LENS Admin Board</strong> for verification. Once approved, your profile will be rendered live in the network directory.
                </p>
              </div>

              {errorMessage && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl text-sm flex items-start gap-3">
                  <Info className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">Application Could Not Be Submitted</p>
                    <p className="mt-0.5">{errorMessage}</p>
                  </div>
                </div>
              )}

              {/* Honeypot */}
              <input
                type="text"
                name="company_website"
                value={companyWebsite}
                onChange={(e) => setCompanyWebsite(e.target.value)}
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
              />

              <Turnstile 
                onVerify={(token) => { setTurnstileToken(token); setTurnstileError(null); }}
                onExpire={() => { setTurnstileToken(''); setTurnstileError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
                onError={() => { setTurnstileToken(''); setTurnstileError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
                resetKey={resetKey} 
              />
              {turnstileError && (
                <p className="text-sm text-rule-3 font-medium text-center">{turnstileError}</p>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || !turnstileToken}
                className="w-full h-14 text-base font-bold gap-2 shadow-lg shadow-rule-3/10 disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
                {isSubmitting ? 'Submitting Application...' : (!turnstileToken ? 'Verifying…' : 'Submit Practitioner Application')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
